import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, Form, Formik } from 'formik';
import { injectStripe } from 'react-stripe-elements';

import { Box, Button, Text, ShadowBox } from '@components/common';
import {
  StripeExpiryField,
  StripeCVCField,
  StripeCardField
} from '@components/form';
import DefaultLoader from '@components/loading/DefaultLoader';
import TimeWindowModal from '@containers/booking/TimeWindowModal';

import CouponField from '../CouponField';
import PaymentSelection from '../PaymentSelection';
import { renderServiceItem } from '../commonRenders';

import { AppSelectors } from '@redux/AppRedux';
import AuthActions, { AuthSelectors } from '@redux/AuthRedux';
import BookingActions, { BookingSelectors } from '@redux/BookingRedux';
import CleansActions, { CleansSelectors } from '@redux/CleansRedux';

import { transfromPropertyToAddress } from '@services/transform';
import { load as loadPixel } from '@services/pixel';
import {
  trackOnetimeSrvsBooked,
  trackCleaningUpdated
} from '@services/analytics';

import API from '@utils/api';
import { StripeConfig } from '@utils/config';
import logger from '@utils/logger';
import { getErrors } from '@utils/error';
import { SURCHARGE_TEXT } from '@services/minte';

import validationSchema from './schema';

class SingleCheckoutForm extends Component {
  constructor(props) {
    super(props);
    const { isModifyingRecurring, currentClean } = props;
    let costs = {};

    if (isModifyingRecurring) {
      costs = currentClean.cost;
    }

    this.state = {
      costs,
      showTimeModal: false,
      costPayload: {},
      isCalculating: true
    };
  }

  async componentDidMount() {
    const {
      addons,
      serviceType,
      isPMUser,
      recipient,
      isModifyingRecurring,
      currentClean,
      serviceDate,
      property
    } = this.props;

    const { propertyID } = property;

    try {
      if (!isModifyingRecurring) {
        const costPayload = {
          addons: [...addons],
          serviceType,
          isPMUser,
          recipient,
          serviceDate: serviceDate.ymdString,
          propertyID
        };
        const pp = await API.getCostCalculations(costPayload);
        this.setState({ costs: pp.result, costPayload, isCalculating: false });
      } else {
        const {
          bookingID,
          timeframe,
          cost: { day, week }
        } = currentClean;
        const costPayload = {
          additionalService: [...addons],
          serviceType,
          isPMUser,
          recipient,
          day,
          week,
          timeframe,
          serviceDate: serviceDate.ymdString,
          propertyID
        };
        const pp = await API.getModifyingCleanCostCalculations(
          bookingID,
          costPayload
        );
        this.setState({ costs: pp.result, costPayload, isCalculating: false });
      }
    } catch (err) {
      this.setState({ isCalculating: false });
    }
  }

  handleCostUpdate = newCost => {
    this.setState({ costs: newCost });
  };

  renderPaymentForm() {
    return (
      <Box>
        <Text fontSize={1} ml={3} mb={3}>
          Card details
        </Text>
        <Field component={StripeCardField} mb={4} name="cardNumValid" />
        <Box flexDirection="row" display="flex" justifyContent="space-between">
          <Field
            component={StripeExpiryField}
            mr={4}
            mb={4}
            name="cardExpValid"
          />
          <Field component={StripeCVCField} mb={4} name="cardCVCValid" />
        </Box>
      </Box>
    );
  }

  setShowTimeModal = () => {
    this.setState({
      showTimeModal: true
    });
  };

  renderPaymentSummary = formValues => {
    const {
      addons,
      serviceType,
      appData,
      serviceDate,
      updateServiceDate,
      property
    } = this.props;
    if (!serviceType) {
      return null;
    }
    const { costs } = this.state;
    const { cost: totalCost, couponDetails, discountDetails } = costs || {};

    const { momentDate, timeframe, availableWindow } = serviceDate;

    const { paymentMethod } = formValues || {};

    const fullAddress = transfromPropertyToAddress(property);

    return (
      <Box>
        <Text fontSize={1} ml={3} mb={3}>
          Your Service
        </Text>

        <Text fontSize={1} ml={3} mb={3}>
          {fullAddress}
        </Text>

        <ShadowBox mb={4}>
          {renderServiceItem(
            appData,
            property,
            serviceType,
            addons,
            momentDate,
            totalCost,
            discountDetails,
            couponDetails,
            paymentMethod,
            timeframe,
            this.setShowTimeModal
          )}
        </ShadowBox>

        <TimeWindowModal
          dismiss={() => {
            this.setState({
              showTimeModal: false
            });
          }}
          show={this.state.showTimeModal}
          timeframe={timeframe}
          availableWindow={availableWindow}
          onSubmit={newTimeframe => {
            updateServiceDate({
              ...serviceDate,
              timeframe: newTimeframe
            });
            this.setState({
              showTimeModal: false
            });
          }}
        />
      </Box>
    );
  };

  handleSubmit = async (values, actions) => {
    const { setSubmitting, setStatus } = actions;
    const {
      stripe,
      user,
      hasPaymentMethod,
      serviceType,
      serviceDate,
      addons,
      refreshUser,
      onSubmit,
      isPMUser,
      recipient,
      existingBookingId,
      setBookingId,
      updateCurrentClean,
      property
    } = this.props;

    setSubmitting(true);

    const { paymentMethod, couponInfo } = values;
    const { propertyID } = property;
    const { coupon } = couponInfo || {};

    let sourceId = '';
    if (!hasPaymentMethod && paymentMethod !== 'invoice') {
      try {
        const { source } = await stripe.createSource({
          type: 'card',
          owner: {
            name: `${user.firstName} ${user.lastName}`
          }
        });

        sourceId = source.id;
      } catch (err) {
        logger.log(err.message);
        return;
      }
    }

    if (sourceId) {
      try {
        await API.addPayment({
          token: StripeConfig.isProduction ? sourceId : 'tok_visa',
          isApplePay: false,
          isGooglePay: false
        });
      } catch (err) {
        const error = getErrors(err);
        if (error.message) {
          setStatus(error);
        } else {
          setStatus({
            message:
              Object.values(error).pop() ||
              'There was an error in updating payment info'
          });
        }
        setSubmitting(false);
        return;
      }
    }

    try {
      if (existingBookingId) {
        const { result } = await API.updateBooking(existingBookingId, {
          coupon: coupon ? coupon : undefined,
          paymentMethod,
          additionalService: [...addons],
          serviceType,
          timeframe: serviceDate.timeframe,
          serviceDate: serviceDate.ymdString,
          isPMUser,
          recipient,
          propertyID
        });
        trackCleaningUpdated(result);
        updateCurrentClean(existingBookingId, result);
      } else {
        const { result } = await API.createBooking({
          coupon: coupon ? coupon : undefined,
          paymentMethod,
          additionalService: [...addons],
          serviceType,
          timeframe: serviceDate.timeframe,
          serviceDate: serviceDate.ymdString,
          isPMUser,
          recipient,
          propertyID
        });
        const { bookingID: bookingId } = result;
        setBookingId(bookingId);
        trackOnetimeSrvsBooked(result, coupon);
        loadPixel(); // tracking for submit-button conversion
      }

      if (!hasPaymentMethod) {
        refreshUser();
      }
      onSubmit();
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setStatus(error);
      } else {
        setStatus({
          message:
            Object.values(error).pop() ||
            'There was an error in creating service'
        });
      }

      if (!hasPaymentMethod) {
        refreshUser();
      }
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
  };

  render() {
    const {
      hasPaymentMethod,
      onCancel,
      isPMUser,
      existingBookingId,
      isModifyingRecurring
    } = this.props;
    const { isCalculating, costPayload } = this.state;

    if (isCalculating) {
      return (
        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="Center"
        >
          <DefaultLoader size={64} />
        </Box>
      );
    }

    const initialValues = {
      isPaymentNeeded: !hasPaymentMethod,
      cardNumValid: false,
      cardExpValid: false,
      cardCVCValid: false,
      paymentMethod: 'credit'
    };

    if (!isPMUser) {
      initialValues.couponInfo = {
        coupon: '',
        isCouponValid: false
      };
    }

    return (
      <Formik
        initialValues={initialValues}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
        initialErrors={validationSchema.isValidSync(initialValues)}
      >
        {({ isValid, isSubmitting, status, values, setFieldValue }) => {
          return (
            <Form>
              <Box display="flex" flexDirection="column">
                <Text fontSize={1} ml={3} mb={3}>
                  Payment Method
                </Text>
                <PaymentSelection
                  selectedMethod={values.paymentMethod}
                  mb={4}
                  onChange={method => {
                    setFieldValue('paymentMethod', method);
                    if (method === 'invoice') {
                      setFieldValue('isPaymentNeeded', false);
                    } else {
                      setFieldValue('isPaymentNeeded', !hasPaymentMethod);
                    }
                  }}
                />

                {!isModifyingRecurring && !isPMUser && (
                  <>
                    <Text fontSize={1} ml={3} mb={3}>
                      Coupons
                    </Text>
                    <Field
                      component={CouponField}
                      onCostUpdate={this.handleCostUpdate}
                      mb={4}
                      name="couponInfo"
                      label="Coupon Code"
                      costPayload={costPayload}
                    />
                  </>
                )}
              </Box>

              {this.renderPaymentSummary(values)}

              {status && status.message && (
                <Text color="red" mb={4}>
                  {status.message}
                </Text>
              )}

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={4}
              >
                {!existingBookingId && (
                  <Button
                    type="button"
                    onClick={onCancel}
                    variant="secondary"
                    mr={4}
                    p={3}
                    flex={1}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  p={3}
                  flex={1}
                  disabled={!isValid || isSubmitting}
                >
                  Confirm
                </Button>
              </Box>
              <Text fontSize={0} color="grey.0" mb={4}>
                Your card will be charged 24 hours before each service. Read
                terms and conditions.
              </Text>
              <Text fontSize={0} color="grey.0" mb={4}>
                {SURCHARGE_TEXT}
              </Text>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

SingleCheckoutForm.propTypes = {
  hasPaymentMethod: PropTypes.bool,
  stripe: PropTypes.any,
  serviceType: PropTypes.string,
  addons: PropTypes.array,
  appData: PropTypes.object,
  serviceDate: PropTypes.object,
  user: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  refreshUser: PropTypes.func,
  isPMUser: PropTypes.bool,
  recipient: PropTypes.any,
  existingBookingId: PropTypes.any,
  setBookingId: PropTypes.func,
  updateServiceDate: PropTypes.func,
  updateCurrentClean: PropTypes.func,
  isModifyingRecurring: PropTypes.bool,
  currentClean: PropTypes.any,
  property: PropTypes.object
};

const mapStatesToProps = state => ({
  appData: AppSelectors.selectAppData(state),
  serviceType: BookingSelectors.selectServiceType(state),
  addons: BookingSelectors.selectAddons(state),
  serviceDate: BookingSelectors.selectServiceDate(state),
  recipient: BookingSelectors.selectRecipientInfo(state),
  user: AuthSelectors.selectCurrentUser(state),
  hasPaymentMethod: AuthSelectors.selectHasPaymentMethod(state),
  isPMUser: AuthSelectors.selectIsPMUser(state),
  existingBookingId: BookingSelectors.selectExistingBookingId(state),
  currentClean: CleansSelectors.selectCurrentClean(state),
  isModifyingRecurring:
    BookingSelectors.selectExistingBookingId(state) &&
    CleansSelectors.isRecurringClean(state),
  property: BookingSelectors.selectCurrentProperty(state)
});

const mapDispatchToProps = dispatch => ({
  refreshUser: () => dispatch(AuthActions.setUserLoadRequest()),
  setBookingId: bookingId => dispatch(BookingActions.setBookingId(bookingId)),
  updateServiceDate: newDate =>
    dispatch(BookingActions.setBookingServiceDate(newDate)),
  updateCurrentClean: (bookingId, result) =>
    dispatch(CleansActions.setMyCleanDetailUpdateRequest(bookingId, result))
});

const connectedForm = connect(
  mapStatesToProps,
  mapDispatchToProps
)(SingleCheckoutForm);

export default injectStripe(connectedForm);
